import React from 'react'
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { getSectionCssSelectors } from '../../utils/themeSectionSelectors'

const copyToClipboard = async (value) => {
  if (!value) return

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value)
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'absolute'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

export default function ThemeCustomCssPanel({
  settings,
  sections = [],
  onSettingsChange,
}) {
  const currentSettings = settings || {}
  const customCss =
    typeof currentSettings.customCss === 'string'
      ? currentSettings.customCss
      : ''

  const allSelectors = sections.flatMap((section) => {
    const selectors = getSectionCssSelectors(section)
    return selectors.map((selector) => ({
      sectionId: section.id,
      sectionType: section.type,
      selector,
    }))
  })

  const updateCustomCss = (value) => {
    onSettingsChange({
      ...currentSettings,
      customCss: value,
    })
  }

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Home Page Custom CSS
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Use section keys and selectors below. This CSS will be applied only on the themed home page.
        </Alert>

        <TextField
          label="Custom CSS"
          fullWidth
          multiline
          minRows={12}
          value={customCss}
          onChange={(e) => updateCustomCss(e.target.value)}
          placeholder={`.theme-template-home .storefront-section--heroBanner {\n  border: 2px solid red;\n}\n\n.theme-section-key--hero-banner {\n  background: #f8f8f8;\n}`}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Available Selectors
        </Typography>

        <Stack spacing={1}>
          {allSelectors.map((item, index) => (
            <Paper
              key={`${item.sectionId}-${item.selector}-${index}`}
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 2,
                backgroundColor: '#fafafa',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {item.sectionType}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                    }}
                  >
                    {item.selector}
                  </Typography>
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => copyToClipboard(item.selector)}
                >
                  Copy
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>
    </Stack>
  )
}